export type CropMethod = 'automatic' | 'manual';

export type CropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  method: CropMethod;
};

type RowStats = { dominantRatio: number; saturation: number; deviation: number };
type HorizontalBounds = { left: number; right: number };

const clamp = (value: number, min = 0, max = 1) => Math.min(max, Math.max(min, value));

export function clampCropRect(rect: CropRect, minimum = .06): CropRect {
  const width = clamp(rect.width, minimum, 1);
  const height = clamp(rect.height, minimum, 1);
  return { ...rect, x: clamp(rect.x, 0, 1 - width), y: clamp(rect.y, 0, 1 - height), width, height };
}

function rowStats(data: Uint8ClampedArray, width: number, y: number, left = 0, right = width): RowStats {
  const bins = new Map<number, number>();
  const step = Math.max(1, Math.floor((right - left) / 180));
  let count = 0; let luminance = 0; let luminanceSquared = 0; let saturation = 0;
  for (let x = left; x < right; x += step) {
    const offset = (y * width + x) * 4;
    const r = data[offset]; const g = data[offset + 1]; const b = data[offset + 2];
    const max = Math.max(r, g, b); const min = Math.min(r, g, b);
    const light = .2126 * r + .7152 * g + .0722 * b;
    luminance += light; luminanceSquared += light * light; saturation += max ? (max - min) / max : 0;
    const key = (r >> 4) << 8 | (g >> 4) << 4 | (b >> 4);
    bins.set(key, (bins.get(key) || 0) + 1); count++;
  }
  const mean = luminance / count;
  return {
    dominantRatio: Math.max(...bins.values()) / count,
    saturation: saturation / count,
    deviation: Math.sqrt(Math.max(0, luminanceSquared / count - mean * mean)),
  };
}

function findHorizontalContentBounds(data: Uint8ClampedArray, width: number, height: number): HorizontalBounds {
  const top = Math.round(height * .06); const bottom = Math.round(height * .94);
  const stepY = Math.max(2, Math.floor((bottom - top) / 220));
  const active = Array.from({ length: width }, (_, x) => {
    const bins = new Map<number, number>(); let count = 0;
    for (let y = top; y < bottom; y += stepY) {
      const offset = (y * width + x) * 4;
      const r = data[offset]; const g = data[offset + 1]; const b = data[offset + 2];
      const key = (r >> 4) << 8 | (g >> 4) << 4 | (b >> 4);
      bins.set(key, (bins.get(key) || 0) + 1); count++;
    }
    return count > 0 && Math.max(...bins.values()) / count < .7;
  });
  const bridge = Math.max(2, Math.round(width * .012));
  const runs: HorizontalBounds[] = []; let start = -1; let lastActive = -1;
  for (let x = 0; x <= width; x++) {
    if (x < width && active[x]) { if (start < 0) start = x; lastActive = x; continue; }
    if (start >= 0 && x < width && x - lastActive <= bridge) continue;
    if (start >= 0) runs.push({ left: start, right: lastActive + 1 });
    start = -1; lastActive = -1;
  }
  const best = runs.sort((a, b) => (b.right - b.left) - (a.right - a.left))[0];
  if (!best || best.right - best.left < width * .45) return { left: 0, right: width };
  const padding = Math.max(1, Math.round(width * .006));
  return { left: Math.max(0, best.left - padding), right: Math.min(width, best.right + padding) };
}

function regionLooksPhotographic(data: Uint8ClampedArray, width: number, top: number, bottom: number, left: number, right: number) {
  const bins = new Set<number>();
  const stepX = Math.max(2, Math.floor((right - left) / 100));
  const stepY = Math.max(2, Math.floor((bottom - top) / 90));
  let count = 0; let luminance = 0; let luminanceSquared = 0; let saturation = 0;
  for (let y = top; y < bottom; y += stepY) for (let x = left; x < right; x += stepX) {
    const offset = (y * width + x) * 4;
    const r = data[offset]; const g = data[offset + 1]; const b = data[offset + 2];
    const max = Math.max(r, g, b); const min = Math.min(r, g, b); const light = .2126 * r + .7152 * g + .0722 * b;
    bins.add((r >> 5) << 6 | (g >> 5) << 3 | (b >> 5));
    saturation += max ? (max - min) / max : 0; luminance += light; luminanceSquared += light * light; count++;
  }
  if (!count) return false;
  const mean = luminance / count; const deviation = Math.sqrt(Math.max(0, luminanceSquared / count - mean * mean));
  return bins.size >= 22 && (deviation >= 25 || saturation / count >= .16);
}

/**
 * Szuka dużych obszarów fotograficznych rozdzielonych jasnymi/szarymi paskami UI.
 * Wynik jest wyłącznie propozycją do zatwierdzenia przez użytkownika.
 */
export function suggestPhotoRegions(data: Uint8ClampedArray, width: number, height: number): CropRect[] {
  if (width < 40 || height < 80 || data.length < width * height * 4) return [];
  const { left, right } = findHorizontalContentBounds(data, width, height);
  const separatorRows = Array.from({ length: height }, (_, y) => {
    const stats = rowStats(data, width, y, left, right);
    return stats.dominantRatio >= .64 && stats.saturation <= .18 && stats.deviation <= 58;
  });
  const minimumBand = Math.max(5, Math.round(height * .012));
  const bands: Array<{ top: number; bottom: number }> = [];
  let start = -1; let lastMatch = -1;
  for (let y = 0; y <= height; y++) {
    const match = y < height && separatorRows[y];
    if (match) { if (start < 0) start = y; lastMatch = y; continue; }
    if (start >= 0 && y - lastMatch <= 2 && y < height) continue;
    if (start >= 0 && lastMatch - start + 1 >= minimumBand) bands.push({ top: start, bottom: lastMatch + 1 });
    start = -1; lastMatch = -1;
  }
  const mergedBands: typeof bands = [];
  for (const band of bands) {
    const previous = mergedBands.at(-1);
    if (previous && band.top - previous.bottom <= 3) previous.bottom = band.bottom;
    else mergedBands.push({ ...band });
  }
  // Krótkie paski nagłówka i karuzele miniaturek bywają kolorowe jak zdjęcie.
  // Automatycznie proponujemy tylko duże obszary; mniejsze użytkownik nadal może
  // zaznaczyć ręcznie bez utraty oryginału.
  const minimumPhotoHeight = Math.max(45, Math.round(height * .15));
  const spans: Array<{ top: number; bottom: number }> = [];
  let cursor = 0;
  for (const band of mergedBands) {
    if (band.top - cursor >= minimumPhotoHeight) spans.push({ top: cursor, bottom: band.top });
    cursor = Math.max(cursor, band.bottom);
  }
  if (height - cursor >= minimumPhotoHeight) spans.push({ top: cursor, bottom: height });
  return spans
    .filter(span => regionLooksPhotographic(data, width, span.top, span.bottom, left, right))
    .slice(0, 8)
    .map(span => clampCropRect({
      x: left / width,
      y: span.top / height,
      width: (right - left) / width,
      height: (span.bottom - span.top) / height,
      method: 'automatic',
    }));
}

export async function analyzeImageForCrops(url: string) {
  const image = new Image();
  image.decoding = 'async';
  image.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve(); image.onerror = () => reject(new Error('Nie udało się odczytać zdjęcia.')); image.src = url;
  });
  const width = Math.min(420, image.naturalWidth);
  const height = Math.max(1, Math.round(image.naturalHeight * width / image.naturalWidth));
  const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('Przeglądarka nie obsługuje analizy obrazu.');
  context.drawImage(image, 0, 0, width, height);
  const pixels = context.getImageData(0, 0, width, height).data;
  return { width: image.naturalWidth, height: image.naturalHeight, suggestions: suggestPhotoRegions(pixels, width, height) };
}
